import React, { useState, useRef } from 'react';
import { Entity } from '../types';

interface AnonymizedTextDisplayProps {
  anonymizedText: string;
  originalText: string;
  entities?: Entity[];
  className?: string;
}

const AnonymizedTextDisplay: React.FC<AnonymizedTextDisplayProps> = ({
  anonymizedText,
  originalText,
  entities = [],
  className = ''
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  // Slider range is always 0 to 1
  const scoreRange = { min: 0, max: 1 };

  // Initialize threshold to 0 so all entities are shown by default (score > 0)
  const [scoreThreshold, setScoreThreshold] = useState(0.0);

  const handleMouseEnter = (e: React.MouseEvent) => {
    setIsHovered(true);
    setMousePosition({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isHovered) return;
    
    const deltaX = Math.abs(e.clientX - mousePosition.x);
    const deltaY = Math.abs(e.clientY - mousePosition.y);
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    
    // If mouse moved more than 50 pixels, go back to anonymized view
    if (distance > 50) {
      setIsHovered(false);
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  // Function to render the text with highlighted anonymized elements
  const renderText = () => {
    const anonymizedMatches = anonymizedText.match(/\[([^\]]+)\]/g) || [];
    
    if (anonymizedMatches.length === 0) {
      return <span className="whitespace-pre-wrap">{anonymizedText}</span>;
    }

    let result: React.ReactNode[] = [];
    let lastIndex = 0;
    let entityIndex = 0;

    anonymizedMatches.forEach((match, index) => {
      const matchIndex = anonymizedText.indexOf(match, lastIndex);
      
      // Add text before the match
      if (matchIndex > lastIndex) {
        result.push(
          <span key={`text-${index}`} className="whitespace-pre-wrap">
            {anonymizedText.slice(lastIndex, matchIndex)}
          </span>
        );
      }

      // Check if this entity should be shown (score > threshold)
      // Find the corresponding entity for this match
      const correspondingEntity = entities[entityIndex];
      const shouldShow = correspondingEntity && correspondingEntity.score > scoreThreshold;

      if (shouldShow) {
        // Add the anonymized element with hover functionality
        result.push(
          <span
            key={`anonymized-${index}`}
            className="bg-yellow-200 hover:bg-yellow-300 px-1 py-0.5 rounded cursor-help transition-colors"
            onMouseEnter={handleMouseEnter}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            title={`Score: ${correspondingEntity.score.toFixed(3)}`}
          >
            {match}
          </span>
        );
      } else {
        // Show original text instead of anonymized if score is too high
        const originalMatch = correspondingEntity?.text || match;
        result.push(
          <span key={`original-${index}`} className="whitespace-pre-wrap">
            {originalMatch}
          </span>
        );
      }

      entityIndex++;
      lastIndex = matchIndex + match.length;
    });

    // Add remaining text
    if (lastIndex < anonymizedText.length) {
      result.push(
        <span key="text-end" className="whitespace-pre-wrap">
          {anonymizedText.slice(lastIndex)}
        </span>
      );
    }

    return result;
  };

  return (
    <div 
      ref={containerRef}
      className={`relative ${className}`}
      onMouseMove={handleMouseMove}
    >
      {/* {entities.length > 0 && (
        <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Score threshold: {scoreThreshold.toFixed(3)}
          </label>
          <input
            type="range"
            min={scoreRange.min}
            max={scoreRange.max}
            step={0.01}
            value={scoreThreshold}
            onChange={(e) => setScoreThreshold(parseFloat(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-gray-600"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>0.000</span>
            <span>1.000</span>
          </div>
        </div>
      )} */}
      {isHovered ? (
        <span className="whitespace-pre-wrap">{originalText}</span>
      ) : (
        renderText()
      )}
    </div>
  );
};

export default AnonymizedTextDisplay; 